import { useEffect, useRef, useState } from 'react';
import { ArrowLeftRight, Building2, Info, Network } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveClient } from '@/contexts/ActiveClientContext';
import { useProfile } from '@/contexts/ProfileContext';
import { podeTrocarCliente, podeTrocarRede } from '@/lib/contexto-navegacao';
import { TrocaContextoModal } from './TrocaContextoModal';
import { formatCnpj } from '@/lib/format';
import { cn } from '@/lib/utils';

// Badge no header com cliente (frotista) OU rede (posto) de navegacao
// ativa + tooltip de detalhes + botao switch (condicional). Regras
// (decisao CEO 2026-05-04):
//   - Perfil frotista: mostra cliente ativo (esconde se sem cliente).
//   - Perfil posto: mostra rede ativa (esconde se sem rede).
//   - Perfil admin: badge nao aparece (admin nao opera contexto direto).
//   - Tooltip exibe sub-blocos cliente/rede com CNPJ + master (se houver).
//     Sub-blocos null nem aparecem.
//   - Botao switch (regras em lib/contexto-navegacao.ts):
//       frotista: super admin, quem tem agrupamento de clientes (com ou sem
//                 cliente proprio) ou quem enxerga mais de 1 cliente.
//       posto:    super admin ou rede master, ambos sem rede direta.
//   - Frotista SEM cliente ativo e que pode trocar: chip "Selecionar
//     cliente" no lugar do badge + modal aberto automaticamente 1x por
//     sessao (sem cliente ativo toda tela frotista falha com
//     ERR_CLIENTE_ATIVO_NAO_DEFINIDO).
//   - Posto SEM rede ativa e que pode trocar: chip "Selecionar rede" no
//     lugar do badge (sem ele o admin global nao tinha como escolher rede).

// Flag de UX (nao e credencial): evita reabrir o modal a cada navegacao.
const AUTO_OPEN_KEY = 'just_frotas_auto_selecionar_cliente';

function jaAbriuAutomatico(userId: string): boolean {
  try {
    return window.sessionStorage.getItem(AUTO_OPEN_KEY) === userId;
  } catch {
    return false;
  }
}

function marcarAbertoAutomatico(userId: string): void {
  try {
    window.sessionStorage.setItem(AUTO_OPEN_KEY, userId);
  } catch {
    /* sessionStorage indisponivel: o ref do componente ainda segura a sessao */
  }
}

export function ContextoNavegacaoBadge() {
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { clientes, loading: loadingClientes } = useActiveClient();
  const [modalOpen, setModalOpen] = useState(false);
  const autoAbertoRef = useRef(false);

  const cliente = user?.activeContext?.cliente ?? null;
  const podeTrocarClienteAtual = user ? podeTrocarCliente(user, clientes.length) : false;
  const semClienteAtivo =
    !!user && activeProfile === 'frotista' && !cliente && podeTrocarClienteAtual;
  const semRedeAtiva =
    !!user && activeProfile === 'posto' && !user.activeContext?.rede && podeTrocarRede(user);

  // Auto-abre o seletor 1x por sessao quando o frotista cai sem cliente
  // ativo e ha clientes disponiveis.
  useEffect(() => {
    if (!user || !semClienteAtivo || loadingClientes || clientes.length === 0) return;
    if (autoAbertoRef.current || jaAbriuAutomatico(user.id)) return;
    autoAbertoRef.current = true;
    marcarAbertoAutomatico(user.id);
    setModalOpen(true);
  }, [user, semClienteAtivo, loadingClientes, clientes.length]);

  if (!user) return null;

  // Perfil admin: badge nao aparece. Idem se perfil ainda nao foi setado.
  if (!activeProfile || activeProfile === 'admin') return null;

  const modal = modalOpen ? (
    <TrocaContextoModal open={modalOpen} onClose={() => setModalOpen(false)} />
  ) : null;

  if (semClienteAtivo) {
    return (
      <>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setModalOpen(true)}
          className="h-7 px-2.5 text-xs gap-1.5"
          data-testid="selecionar-cliente"
        >
          <Building2 className="w-3.5 h-3.5" />
          Selecionar cliente
        </Button>
        {modal}
      </>
    );
  }

  if (semRedeAtiva) {
    return (
      <>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setModalOpen(true)}
          className="h-7 px-2.5 text-xs gap-1.5"
          data-testid="selecionar-rede"
        >
          <Network className="w-3.5 h-3.5" />
          Selecionar rede
        </Button>
        {modal}
      </>
    );
  }

  const ctx = user.activeContext;
  // Sem activeContext (login antigo no cache, ou rota /me ainda nao
  // retornou): nao renderiza nada (header gracioso).
  if (!ctx) return null;

  const rede = ctx.rede;

  // Display por perfil ativo: frotista mostra cliente, posto mostra rede.
  const display =
    activeProfile === 'frotista' && cliente
      ? { label: cliente.nome, icon: Building2, kind: 'cliente' as const }
      : activeProfile === 'posto' && rede
        ? { label: rede.nome, icon: Network, kind: 'rede' as const }
        : null;

  if (!display) return null; // perfil sem contexto correspondente

  const Icon = display.icon;

  const podeTrocar =
    activeProfile === 'frotista' ? podeTrocarClienteAtual : podeTrocarRede(user);

  return (
    <>
      <div className="flex items-center gap-1.5">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold',
            'bg-primary-soft text-primary',
          )}
        >
          <Icon className="w-3 h-3" />
          <span className="truncate max-w-[260px]">{display.label}</span>
        </span>

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Detalhes do contexto de navegação"
              className="text-text-subtle hover:text-text-strong p-1 rounded hover:bg-surface-soft transition"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 p-0">
            <ContextoTooltipBody
              cliente={cliente}
              rede={rede}
              roles={user.roles ?? []}
              isAdmin={!!user.isAdmin}
            />
          </PopoverContent>
        </Popover>

        {podeTrocar ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setModalOpen(true)}
            className="h-7 px-2 text-xs gap-1"
            aria-label="Trocar contexto de navegação"
          >
            <ArrowLeftRight className="w-3.5 h-3.5" />
            Trocar
          </Button>
        ) : null}
      </div>

      {modal}
    </>
  );
}

function ContextoTooltipBody({
  cliente,
  rede,
  roles,
  isAdmin,
}: {
  cliente: { nome: string; cnpj: string | null; clienteMaster: { nome: string } | null } | null;
  rede: { nome: string; cnpj: string | null; redeMaster: { nome: string } | null } | null;
  roles: string[];
  isAdmin: boolean;
}) {
  // Filtra 'admin' dos roles user-facing - exibido como badge proprio.
  // Demais roles (sem o sentinel) aparecem como pills neutros.
  const userRoles = roles.filter((r) => r !== 'admin');

  return (
    <div className="divide-y divide-border">
      {cliente ? (
        <section className="p-4 space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-text-subtle font-semibold">
            Cliente
          </p>
          <p className="text-sm font-semibold text-text-strong">{cliente.nome}</p>
          {cliente.cnpj ? (
            <p className="text-xs text-text-muted font-mono">{formatCnpj(cliente.cnpj)}</p>
          ) : null}
          {cliente.clienteMaster ? (
            <div className="text-xs text-text-muted pt-1">
              <span className="text-text-subtle">Cliente Master: </span>
              <span className="text-text-strong">{cliente.clienteMaster.nome}</span>
            </div>
          ) : null}
        </section>
      ) : null}
      {rede ? (
        <section className="p-4 space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-text-subtle font-semibold">
            Rede
          </p>
          <p className="text-sm font-semibold text-text-strong">{rede.nome}</p>
          {rede.cnpj ? (
            <p className="text-xs text-text-muted font-mono">{formatCnpj(rede.cnpj)}</p>
          ) : null}
          {rede.redeMaster ? (
            <div className="text-xs text-text-muted pt-1">
              <span className="text-text-subtle">Rede Master: </span>
              <span className="text-text-strong">{rede.redeMaster.nome}</span>
            </div>
          ) : null}
        </section>
      ) : null}
      {/* Section de perfis de acesso (read-only). Sempre aparece quando
          user tem isAdmin OU pelo menos 1 role nao-admin. Pra info do
          usuario saber qual "perfil" esta acessando. */}
      {(isAdmin || userRoles.length > 0) ? (
        <section className="p-4 space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-text-subtle font-semibold">
            Perfis de acesso
          </p>
          <div className="flex flex-wrap gap-1.5">
            {isAdmin ? (
              <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-danger-bg text-danger-text font-semibold border border-danger/20">
                Super administrador
              </span>
            ) : null}
            {userRoles.map((r) => (
              <span
                key={r}
                className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-surface-soft text-text-strong border border-border"
              >
                {r}
              </span>
            ))}
          </div>
        </section>
      ) : null}
      {!cliente && !rede && !isAdmin && userRoles.length === 0 ? (
        <section className="p-4">
          <p className="text-xs text-text-muted">Nenhum contexto de navegação ativo.</p>
        </section>
      ) : null}
    </div>
  );
}
